import { Heading, Text } from '@chakra-ui/react';
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';

import { Content } from '@/components/templatePage/content';
import { Header } from '@/components/templatePage/header';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { generateStore } from '@/utils/generateStore';

const About: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ accessFromLocation }) => {
  return (
    <Fragment>
      <NextSeo
        title="About Underline | Scientific Conference Organizer & Digital Video Library"
        titleTemplate="%s"
        description="Underline is a professional conference organizer, hybrid event platform, and digital video library for scientific, academic, medical, and society events."
        openGraph={{
          description:
            'Underline is a professional conference organizer, hybrid event platform, and digital video library for scientific, academic, medical, and society events.',
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/about`,
        }}
      />
      <Layout accessFrom={accessFromLocation}>
        <Header>
          About Underline: Scientific Event
          <br />
          Platform and Digital Video Library
        </Header>
        <Content>
          <Text>
            Underline helps scientific, academic, medical, and professional communities share knowledge before, during,
            and long after a conference ends. We combine two connected products: Underline Events, a professional
            conference organizer and event platform tailored for scientific events, and the Underline Digital Video
            Library, a searchable academic video repository built for lectures, keynotes, research presentations,
            panels, posters, papers, slides, transcripts, and conference proceedings. Our purpose is simple: make
            scientific knowledge easier to organize, easier to attend, easier to discover, easier to cite, and easier to
            preserve.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            A Professional Conference Organizer for Scientific, Academic, and Medical Events{' '}
          </Heading>
          <Text>
            Underline Events supports scientific conferences, academic events, medical meetings, research congresses,
            professional society conferences, and technical symposia in every format:{' '}
            <Text as="span" fontWeight="bold">
              in-person, virtual, and hybrid.
            </Text>
          </Text>
          <Text>
            Unlike general event vendors, Underline focuses on scientific and scholarly events. These meetings have
            different requirements: parallel tracks, technical talks, speaker permissions, poster sessions, proceedings,
            society governance, academic audiences, global presenters, remote attendees, livestreaming, recordings,
            searchable archives, and post-event access.
          </Text>
          <Text>
            We help organizers plan, produce, stream, record, manage, and extend the value of their conference content.{' '}
          </Text>
          <Text as="span">Our event services include:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>Scientific conference planning and event management</li>
            <li>Hybrid event production and virtual event delivery</li>
            <li>In-person conference support and audio-visual services</li>
            <li>Conference livestreaming and session recording</li>
            <li>Speaker, session, and content coordination</li>
            <li>Online schedules, access control, and attendee engagement tools</li>
            <li>Post-event video publishing and long-term content preservation</li>
            <li>Event monetization and content library strategy</li>
          </Text>
          <Text>
            Whether a society needs a full-service professional conference organizer, a hybrid conference platform, AV
            support, livestreaming, or a permanent video archive, Underline provides an integrated solution built
            specifically for science.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Hybrid Events Built for Scientific Conferences
          </Heading>
          <Text>
            Scientific events are global by nature. Not every speaker, researcher, student, clinician, or attendee can
            travel, but their work still deserves to be seen and heard.{' '}
          </Text>
          <Text>
            Underline’s hybrid event platform connects in-person and remote audiences through livestreaming, recordings,
            online access, interactive tools, and structured post-event content. Remote attendees can follow sessions,
            engage with talks, and access recordings after the event. In-person attendees can revisit sessions they
            missed. Organizers can expand reach without losing control of the conference experience.
          </Text>
          <Text>
            For scientific societies, hybrid events are not just a convenience. They are a way to increase
            participation, improve accessibility, extend impact, and preserve the intellectual value of the meeting.{' '}
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            The Underline Digital Video Library
          </Heading>
          <Text>
            The Underline Digital Video Library is a scholarly video platform for scientific and academic content. It
            helps researchers, students, librarians, societies, publishers, and institutions discover and use conference
            knowledge that would otherwise disappear after the event.
          </Text>
          <Text>
            The library includes scientific lectures, academic presentations, medical conference videos, research talks,
            keynote sessions, panels, tutorials, workshops, posters, slides, papers, proceedings, and transcripts. Each
            presentation can become a structured academic record with searchable metadata, transcripts, linked
            materials, and citation support. Researchers can find relevant talks, follow ideas across conferences, cite
            presentations, share content, and connect with speakers or collaborators.
          </Text>
          <Text>The Underline library is designed for scholarly discovery, not generic video hosting.</Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Turning Conference Videos into Citable Scientific Knowledge
          </Heading>
          <Text>
            A conference presentation is often where research appears before it becomes a journal article, technical
            report, standard, product, treatment protocol, or new collaboration. Underline helps preserve that
            early-stage knowledge and make it useful.
          </Text>
          <Text as="span">Our platform enriches scientific videos with:</Text>
          <Text as="ul" listStyleType="disc" ps={6} mt={0}>
            <li>Searchable transcripts</li>
            <li>Speaker and session metadata</li>
            <li>Linked abstracts, papers, posters, slides, and PowerPoint presentations</li>
            <li>DOI support for citation and referencing</li>
            <li>Downloadable transcripts and supporting materials</li>
            <li>MARC records for libraries</li>
            <li>Accessibility support aligned with WCAG standards</li>
            <li>Persistent URLs for long-term discovery</li>
            <li>Analytics for usage, reach, and engagement</li>
          </Text>
          <Text>
            This transforms conference content into a long-term research asset for societies, libraries, publishers, and
            researchers.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Built for Societies, Libraries, Researchers, and Publishers
          </Heading>
          <Text>Underline serves the full scientific communication ecosystem.</Text>
          <Text>
            <Text fontWeight="bold" as="span">
              For societies and conference organizers
            </Text>
            , we provide professional conference organization, hybrid event production, virtual event platforms,
            in-person AV support, livestreaming, recording, and post-event video publishing.{' '}
          </Text>
          <Text>
            <Text fontWeight="bold" as="span">
              For libraries
            </Text>
            , we provide a digital video library of scientific and academic research content with searchable
            transcripts, MARC records, DOI-linked lectures, accessible content, and multidisciplinary coverage.{' '}
          </Text>
          <Text>
            <Text fontWeight="bold" as="span">
              For researchers
            </Text>
            , we provide a way to share presentations beyond the conference room, increase discoverability, support
            citation, and connect with collaborators.
          </Text>
          <Text>
            <Text fontWeight="bold" as="span">
              For publishers and institutions
            </Text>
            , we provide a platform to preserve, distribute, and monetize high-value conference content while expanding
            global access to scientific knowledge.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Scientific Conferences Only
          </Heading>
          <Text>
            Underline is focused on science, medicine, academia, engineering, technology, and professional research
            communities. That focus matters.
          </Text>
          <Text>
            Scientific events are complex. They require precision, speaker coordination, technical reliability,
            metadata, content rights, searchable archives, library standards, citation infrastructure, and long-term
            preservation. General event platforms are usually built for marketing webinars, corporate meetings, or trade
            shows. Underline is built for scientific conferences and academic events from the ground up.
          </Text>
          <Text>
            That specialization allows us to deliver better workflows, better content structure, better post-event
            value, and better support for the audiences that scientific societies care about most.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Our Mission
          </Heading>
          <Text>
            Our mission is to digitize, preserve, and expand access to scientific knowledge through video. We believe
            the value of a scientific conference should not end when the last session closes. Every lecture, keynote,
            panel, poster, and presentation can continue to educate, inspire, and connect researchers around the world.
          </Text>
          <Text>Underline exists to make that possible.</Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Why Underline
          </Heading>
          <Text>
            Scientific communities choose Underline because we bring together event execution, video technology,
            scholarly metadata, digital library infrastructure, and scientific publishing experience in one platform.
          </Text>
          <Text as="span">We help clients:</Text>
          <Text as="ul" listStyleType="disc" ps={6} mt={0}>
            <li>Run better scientific, academic, and medical events</li>
            <li>Deliver professional in-person, virtual, and hybrid conferences</li>
            <li>Reduce the operational burden on conference organizers</li>
            <li>Improve the attendee and speaker experience</li>
            <li>Capture and preserve conference knowledge</li>
            <li>Make research videos searchable and citable</li>
            <li>Expand global access to scientific presentations</li>
            <li>Create long-term value from conference content</li>
          </Text>
          <Text>Underline is where scientific events become lasting digital knowledge.</Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Work With Underline
          </Heading>
          <Text>
            If you are planning a scientific conference, academic event, medical meeting, hybrid conference, virtual
            event, or digital video library for your society, institution, publisher, or research community, Underline
            can help.
          </Text>
          <Text>
            We support the full lifecycle: event planning, production, livestreaming, recording, platform access,
            content enrichment, searchable video publishing, library distribution, and long-term preservation.
          </Text>
          <Heading as="h3" fontSize="2xl" my={10}>
            Underline Events. Underline Digital Video Library. One platform for scientific knowledge.
          </Heading>
        </Content>
      </Layout>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.res.getHeader(accessFrom) || AccessFrom.Core,
    },
  };
};

export default About;
